import { Badge, cn } from '@herit-fe/heritage-ui';
import type { EventGrade } from '@/shared/data-types';
import { UI_TEXT } from '@/shared/languages';
import { STATUS_GLYPH_COLOR, StatusGlyph, type StatusGlyphKey } from './status-glyph';

/**
 * 등급 라벨 — 알림 카드/표/대시보드 요약 공유(2+ 위젯이 쓰므로 shared 거주). 구앱 `EventGradeLabel` 정합.
 *
 * `inline`(기본) = 상태 글리프(`StatusGlyph` SSOT) + 상태 팔레트 컬러 텍스트. 카드/표처럼 등급을 문장으로
 * 읽는 자리. `resolved` 면 정상(체크·success) "해소" 라벨 동반.
 * `pill` = 상태색 채운 알약. 한 줄 목록에서 등급을 **한눈에 스캔**해야 하는 자리(대시보드 최근 이상알림).
 * 아이콘·색은 발전소 상태(`StatusIcon`)와 동일 SSOT(글리프 path·`STATUS_GLYPH_COLOR`).
 */
const GRADE_GLYPH: Record<EventGrade, StatusGlyphKey> = {
  critical: 'critical',
  warning: 'warning',
  commLost: 'disconnected',
};

const GRADE_LABEL: Record<EventGrade, string> = {
  critical: UI_TEXT.STATUS_CRITICAL,
  warning: UI_TEXT.STATUS_WARNING,
  commLost: UI_TEXT.STATUS_COMM_LOST,
};

/** pill 배경 — 글리프 색과 같은 상태 토큰의 면(面) 짝. 대비는 `*-foreground` 가 보장. */
const GRADE_PILL: Record<EventGrade, string> = {
  critical: 'bg-heviton-error text-heviton-error-foreground',
  warning: 'bg-heviton-warning text-heviton-warning-foreground',
  commLost: 'bg-heviton-none text-heviton-none-foreground',
};

export interface AlertGradeBadgeProps {
  grade: EventGrade;
  /** 해소 알림 — `inline` 에서만 "해소" 라벨을 덧붙인다(pill 은 한 줄 목록이라 등급만). */
  resolved?: boolean;
  variant?: 'inline' | 'pill';
  className?: string;
}

export function AlertGradeBadge({
  grade,
  resolved,
  variant = 'inline',
  className,
}: AlertGradeBadgeProps) {
  const glyph = GRADE_GLYPH[grade];

  if (variant === 'pill') {
    // 해소는 등급색을 쓰지 않는다 — 이미 풀린 건이 심각(빨강)으로 보이면 미해소 건과 시각적으로 경쟁한다.
    // 무상태(회색)로 낮춰 "지나간 일"로 읽히게.
    return (
      <>
        <Badge
          className={cn(
            'p-1.5 py-0 text-[11px] min-h-0 h-auto leading-normal',
            resolved ? 'bg-heviton-none text-heviton-none-foreground' : GRADE_PILL[grade],
            className,
          )}
        >
          {resolved ? UI_TEXT.STATUS_RESOLVED : GRADE_LABEL[grade]}
        </Badge>
      </>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-3 text-sm font-semibold', className)}>
      <span className={cn('inline-flex items-center gap-1', STATUS_GLYPH_COLOR[glyph])}>
        <StatusGlyph glyph={glyph} className="size-4" />
        {GRADE_LABEL[grade]}
      </span>
      {resolved && (
        <span className={cn('inline-flex items-center gap-1', STATUS_GLYPH_COLOR.normal)}>
          <StatusGlyph glyph="normal" className="size-4" />
          {UI_TEXT.STATUS_RESOLVED}
        </span>
      )}
    </span>
  );
}
